import { blackOrange, darkOrange, gold, orange, vanilla } from '@/theme/colors'
import styled from 'styled-components'

export const TabRow = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    border-bottom: 1px solid #f0e0d6;
`

export const Tab = styled.button<{ $active: boolean }>`
    background: none;
    border: none;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: ${({ $active }) => ($active ? darkOrange : '#6b7280')};
    cursor: pointer;
    border-bottom: 2px solid ${({ $active }) => ($active ? orange : 'transparent')};
    transition: color 0.15s;

    &:hover {
        color: ${darkOrange};
    }
`

export const LegsSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
`

export const LegsSummaryTitle = styled.div`
    font-size: 0.75rem;
    font-weight: 700;
    color: ${gold};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.25rem;
`

export const LegSummaryRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: ${vanilla};
    border-radius: 8px;
`

export const LegSummaryText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

export const LegSummaryCode = styled.span`
    font-size: 0.8125rem;
    font-weight: 700;
    color: #262626;
`

export const LegSummaryRoute = styled.span`
    font-size: 0.75rem;
    color: ${blackOrange};
`

export const LegSummaryTime = styled.span`
    font-size: 0.6875rem;
    color: #6b7280;
`

export const AddAnotherLegButton = styled.button`
    background: none;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
    color: ${gold};
    cursor: pointer;
    padding: 0.625rem;
    font-size: 0.8125rem;
    font-weight: 500;
    transition: all 0.15s;

    &:hover {
        border-color: ${orange};
        color: ${orange};
        background: #fef3e2;
    }
`

export const ManualFallbackNotice = styled.div`
    padding: 0.75rem 1rem;
    background: #fef3e2;
    border: 1px solid ${orange}40;
    border-radius: 8px;
    font-size: 0.8125rem;
    color: ${blackOrange};
    margin-bottom: 1rem;
    line-height: 1.4;
`

export const FormGroup = styled.div`
    margin-bottom: 1.25rem;

    &:last-child {
        margin-bottom: 0;
    }
`

export const FormLabel = styled.label`
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
`

export const FormInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 16px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
`

export const FormDescription = styled.p`
    font-size: 0.75rem;
    color: #9ca3af;
    margin: -4px 0 8px;
`

export const FieldRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    @media (max-width: 500px) {
        grid-template-columns: 1fr;
    }
`

export const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 1rem;
`

export const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;

    ${({ variant = 'secondary' }) => {
		switch (variant) {
			case 'primary':
				return `
                    background-color: ${orange};
                    color: white;
                    &:hover:not(:disabled) { opacity: 0.9; }
                    &:disabled { opacity: 0.5; cursor: not-allowed; }
                `
			case 'danger':
				return `
                    background-color: #dc2626;
                    color: white;
                    &:hover:not(:disabled) { background-color: #b91c1c; }
                `
			default:
				return `
                    background-color: white;
                    color: #374151;
                    border: 1px solid #d1d5db;
                    &:hover { background-color: #f3f4f6; }
                `
		}
	}}
`

export const PassengersList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
`

export const PassengerRowStyled = styled.div<{ $isSelected: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;
    background: ${({ $isSelected }) => ($isSelected ? '#fef3e2' : 'transparent')};

    &:hover {
        background: ${({ $isSelected }) => ($isSelected ? '#fef3e2' : '#f9fafb')};
    }
`

export const PassengerCheckbox = styled.div<{ $checked: boolean }>`
    width: 20px;
    height: 20px;
    border-radius: 4px;
    border: 2px solid ${({ $checked }) => ($checked ? orange : '#d1d5db')};
    background: ${({ $checked }) => ($checked ? orange : 'white')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;

    &::after {
        content: '${({ $checked }) => ($checked ? '\\2713' : '')}';
        color: white;
        font-size: 12px;
        font-weight: 700;
    }
`

export const ProfileAvatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${orange};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    flex-shrink: 0;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

export const PassengerName = styled.span`
    font-size: 0.875rem;
    font-weight: 500;
    color: #262626;
    flex: 1;
`

export const SummaryBox = styled.div`
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f9fafb;
    border-radius: 6px;
`

export const ErrorText = styled.span`
    color: #dc2626;
    font-weight: 500;
`
